import {APP_INITIALIZER, ErrorHandler, NgModule, isDevMode} from '@angular/core';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import {BrowserModule} from '@angular/platform-browser';
import {
  AuthInterceptor as OidcAuthInterceptor,
  AuthModule,
  OidcSecurityService,
  OpenIdConfiguration,
  StsConfigHttpLoader,
  StsConfigLoader
} from 'angular-auth-oidc-client';
import {LoggerModule, NGXLogger, NgxLoggerLevel} from 'ngx-logger';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {environment} from '../environments/environment';
import {ConfigModule} from './config/config.module';
import {ConfigService} from './config/service/config.service';
// OpenAPI Shell Service
import {
  ApiModule as ShellServiceApiModule,
  Configuration as ShellServiceConfiguration,
} from './openapi/shell-service-openapi';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import * as Sentry from '@sentry/angular-ivy';
import {filter, tap} from 'rxjs/operators';
import {map, take} from 'rxjs';
import jwtDecode, {JwtPayload} from 'jwt-decode';
import {EmbeddedService} from './embedded/embedded.service';
import {AuthInterceptor} from './auth/auth.interceptor';
import {EulaModule} from './eula/eula.module';
import {
  MatomoConsentMode,
  MatomoInitializationMode,
  MatomoInitializerService,
  NgxMatomoTrackerModule,
} from '@ngx-matomo/tracker';
import {NgxMatomoRouterModule} from '@ngx-matomo/router';
import {ContextInfoService} from './iframe-apps/context-info.service';
import {MatDialogModule} from '@angular/material/dialog';
import {NGX_LOGGER, provideDialogs, provideTheme} from '@adnova/jf-ng-components';
import {registerLocaleData} from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import {provideStore, provideState} from '@ngrx/store';
import {provideEffects} from '@ngrx/effects';
import {provideStoreDevtools} from '@ngrx/store-devtools';
import {appReducers} from './store/app';
import {dialogsReducers, DIALOGS_FEATURE_KEY} from './modules/dialogs/+store';
import {
  DeleteAnnouncementDialogActions,
  DeleteAnnouncementDialogSelectors,
} from './modules/dialogs/delete-announcement-dialog';
import {
  InfoPopupDialogActions,
  InfoPopupDialogSelectors,
} from './modules/dialogs/info-popup-dialog';
import {
  ChangeCredentialsDialogActions,
  ChangeCredentialsDialogSelectors,
} from './modules/dialogs/change-credentials-dialog/+store';
import {
  AnalyticsConsentDialogActions,
  AnalyticsConsentDialogSelectors,
} from './modules/dialogs/analytics-consent-dialog/+store';
import {
  LogoutDialogActions,
  LogoutDialogEffects,
  LogoutDialogSelectors,
} from './modules/dialogs/logout-dialog/+store';
import {
  TimeDeviationDialogActions,
  TimeDeviationDialogSelectors,
} from './modules/dialogs/time-deviation-dialog/+store';
import {
  AppChooserDialogActions,
  AppChooserDialogEffects,
  AppChooserDialogSelectors,
} from './modules/dialogs/app-chooser-dialog/+store';
import { AnnouncementEntitiesEffects } from './store/announcement-entities';
import { AnnouncementDetailEntitiesEffects } from './store/announcement-detail-entities';
import { UserAnnouncementEntitiesEffects } from './store/user-announcement-entities';
import { AnnouncementArchiveEntitiesEffects } from './store/announcement-archive-entities';
import { ENTITIES_FEATURE_KEY, entitiesReducers } from './store/entities';
import { ASSISTANT_FEATURE_KEY, assistantReducers, AssistantEffects } from './assistant/+store';
import {
  ADMIN_ANNOUNCEMENT_FEATURE_KEY,
  adminAnnouncementReducers,
  AdminAnnouncementEffects,
} from './system-apps/admin-announcement/+store';


registerLocaleData(localeDe, 'de-DE', localeDeExtra);


@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    MatDialogModule,
    AppRoutingModule,
    EulaModule,
    NgbModule,
    // Config
    ConfigModule,
    // OpenAPI Shell Service
    ShellServiceApiModule.forRoot(() => new ShellServiceConfiguration()),
    // Logging
    LoggerModule.forRoot({
      level: NgxLoggerLevel.INFO, // default logging level
      enableSourceMaps: !environment.production,
    }),
    // Auth
    AuthModule.forRoot({
      loader: {
        provide: StsConfigLoader,
        useFactory: (configService: ConfigService) => {
          const configObservable = configService.loaded$
            .pipe(
              take(1),
              map(config => {
                const authConfig: OpenIdConfiguration = {
                  // loaded
                  authority: config.auth.authority,
                  clientId: config.auth.clientId,
                  scope: config.auth.scope,
                  logLevel: config.auth.logLevel,
                  renewTimeBeforeTokenExpiresInSeconds: config.auth.renewTimeBeforeTokenExpiresInSeconds,
                  // INFO: Bei anpassungen auch AuthInterceptor.matchesSecureRoots() anpassen
                  secureRoutes: [config.base.baseUrl, config.shell.selfserviceBaseUrl],
                  // predefined
                  redirectUrl: window.location.origin,
                  postLogoutRedirectUri: window.location.origin,
                  responseType: 'code',
                  silentRenew: true,
                  triggerAuthorizationResultEvent: true,
                  ignoreNonceAfterRefresh: true,
                  useRefreshToken: true,
                };
                return authConfig;
              }),
            );
          return new StsConfigHttpLoader(configObservable);
        },
        deps: [ConfigService],
      },
    }),
    NgxMatomoTrackerModule.forRoot({
      mode: MatomoInitializationMode.AUTO_DEFERRED,
      requireConsent: MatomoConsentMode.TRACKING,
    }),
    NgxMatomoRouterModule,
  ],
  exports: [
    AuthModule,
  ],
  providers: [
    provideStore(
      appReducers,
      {
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
          strictActionSerializability: true,
          strictStateSerializability: true,
        },
      },
    ),
    provideState(DIALOGS_FEATURE_KEY, dialogsReducers),
    provideState(ASSISTANT_FEATURE_KEY, assistantReducers),
    provideState(ENTITIES_FEATURE_KEY, entitiesReducers),
    provideState(ADMIN_ANNOUNCEMENT_FEATURE_KEY, adminAnnouncementReducers),
    provideEffects([AnnouncementEntitiesEffects, AnnouncementDetailEntitiesEffects, AdminAnnouncementEffects, UserAnnouncementEntitiesEffects, AnnouncementArchiveEntitiesEffects, LogoutDialogEffects, AppChooserDialogEffects, AssistantEffects]),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
      autoPause: true,
      trace: false,
      traceLimit: 75,
    }),
    // jf-lib Dialogs
    provideDialogs([
      {
        key: 'deleteAnnouncement',
        isOpenSelector: DeleteAnnouncementDialogSelectors.isOpen,
        closeAction: DeleteAnnouncementDialogActions.close,
        loader: () => import('./modules/dialogs/delete-announcement-dialog/delete-announcement-dialog.component')
          .then(c => c.DeleteAnnouncementDialogComponent),
        config: {
          width: '400px',
          minWidth: '400px',
          maxHeight: '400px',
          disableClose: true,
          autoFocus: false,
          showCloseButton: true,
        },
      },
      {
        key: 'infoPopup',
        isOpenSelector: InfoPopupDialogSelectors.isOpen,
        closeAction: InfoPopupDialogActions.close,
        closeButtonAction: InfoPopupDialogActions.closeViaButton,
        loader: () => import('./modules/dialogs/info-popup-dialog/info-popup-dialog.component')
          .then(c => c.InfoPopupDialogComponent),
        config: {
          width: '420px',
          minWidth: '420px',
          minHeight: '200px',
          maxHeight: '90vh',
          disableClose: false,
          autoFocus: false,
          showCloseButton: true,
          position: { right: '1rem', top: '3rem' },
          panelClass: 'info-popup-panel',
          ariaLabel: 'Neuigkeiten und Infos',
        },
      },
      {
        key: 'changeCredentials',
        isOpenSelector: ChangeCredentialsDialogSelectors.isOpen,
        closeAction: ChangeCredentialsDialogActions.close,
        loader: () => import('./modules/dialogs/change-credentials-dialog/change-credentials-dialog.component')
          .then(c => c.ChangeCredentialsDialogComponent),
        config: {
          minWidth: '300px',
          minHeight: '120px',
          disableClose: false,
          autoFocus: false,
          showCloseButton: true,
        },
      },
      {
        key: 'analyticsConsent',
        isOpenSelector: AnalyticsConsentDialogSelectors.isOpen,
        closeAction: AnalyticsConsentDialogActions.close,
        loader: () => import('./modules/dialogs/analytics-consent-dialog/analytics-consent-dialog.component')
          .then(c => c.AnalyticsConsentDialogComponent),
        config: {
          width: '560px',
          minWidth: '300px',
          minHeight: '400px',
          maxHeight: '6150px',
          disableClose: false,
          autoFocus: false,
          showCloseButton: true,
          fixedActions: true,
          fixedTitle: false,
          panelClass: 'analytics-consent-dialog-panel',
        },
      },
      {
        key: 'logout',
        isOpenSelector: LogoutDialogSelectors.isOpen,
        closeAction: LogoutDialogActions.close,
        loader: () => import('./modules/dialogs/logout-dialog/logout-dialog.component')
          .then(c => c.LogoutDialogComponent),
        config: {
          disableClose: false,
          autoFocus: false,
          showCloseButton: true,
        },
      },
      {
        key: 'timeDeviation',
        isOpenSelector: TimeDeviationDialogSelectors.isOpen,
        closeAction: TimeDeviationDialogActions.close,
        loader: () => import('./modules/dialogs/time-deviation-dialog/time-deviation-dialog.component')
          .then(c => c.TimeDeviationDialogComponent),
        config: {
          width: '500px',
          disableClose: true,
          autoFocus: false,
          showCloseButton: false,
        },
      },
      {
        key: 'appChooser',
        isOpenSelector: AppChooserDialogSelectors.isOpen,
        closeAction: AppChooserDialogActions.close,
        loader: () => import('./modules/dialogs/app-chooser-dialog/app-chooser-dialog.component')
          .then(c => c.AppChooserDialogComponent),
        config: {
          disableClose: false,
          autoFocus: false,
          showCloseButton: true,
        },
      },
    ]),
    provideTheme(),
    OidcAuthInterceptor,
    {
      // INFO: Interceptor um automatisch da OAuth Token zu setzen
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      // INFO: Initializer das Logging
      provide: APP_INITIALIZER,
      useFactory: (configService: ConfigService, logger: NGXLogger) => () => {
        return configService.loaded$
          .pipe(
            take(1),
            tap(value => {
              logger.updateConfig({
                ...logger.getConfigSnapshot(),
                level: value.logging.level
              });
            }),
          );
      },
      deps: [ConfigService, NGXLogger],
      multi: true,
    },
    {
      // INFO: Initializer für Sentry Integration
      provide: APP_INITIALIZER,
      useFactory: (
        configService: ConfigService,
        contextInfoService: ContextInfoService,
        logger: NGXLogger,
      ) => () => {
        return configService.loaded$
          .pipe(
            take(1),
            tap(config => {
              const sentry = config.sentry;
              const info = environment.info;
              if (sentry) {
                Sentry.init({
                  enabled: sentry.enabled,
                  debug: sentry.debug,
                  environment: config.base.environment,
                  release: info.commit,
                  dsn: sentry.dsn,
                  sampleRate: sentry.sampleRate,
                  autoSessionTracking: false,
                  enableTracing: false,
                  maxBreadcrumbs: 100,
                  initialScope: {
                    extra: {
                      'info': info,
                    },
                  },
                  beforeSend: (event) => {
                    if (contextInfoService?.consentErrorReporting) {
                      return event;
                    }
                    logger.debug('Error reporting is disabled, dropping event');
                    return null;
                  },
                  beforeSendTransaction: (event) => {
                    if (contextInfoService?.consentErrorReporting) {
                      return event;
                    }
                    logger.debug('Error reporting is disabled, dropping transaction event');
                    return null;
                  },
                  beforeBreadcrumb: (breadcrumb) => {
                    if (contextInfoService?.consentErrorReporting) {
                      return breadcrumb;
                    }
                    logger.debug('Error reporting is disabled, dropping breadcrumb');
                    return null;
                  },
                });
              }
            }),
          );
      },
      deps: [
        ConfigService,
        ContextInfoService,
        NGXLogger,
      ],
      multi: true,
    },
    {
      // INFO: Angular Sentry ErrorHandler
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      // INFO: Initializer für Portal integration die Base-URL setzt
      provide: APP_INITIALIZER,
      useFactory: (
        configService: ConfigService,
        embeddedService: EmbeddedService,
        oidcSecurityService: OidcSecurityService,
      ) => () => {
        return configService.loaded$
          .pipe(
            take(1),
            tap(() => {
              const runningEmbedded = embeddedService.embedded;
              Sentry.setTag('runningEmbedded', runningEmbedded);

              if (runningEmbedded) {
                embeddedService.addAccessTokenListener(accessToken => {
                  const decoded = jwtDecode<JwtPayload>(accessToken);
                  const token = decoded as any;

                  const email = token.email;
                  const username = token.preferred_username;
                  const name = token.name;

                  Sentry.withScope(scope => {
                    let user = scope.getUser();
                    if (!user) {
                      user = {};
                    }
                    user.id = decoded.sub;
                    user.email = email;
                    user.username = username;
                    user['name'] = name;

                    scope.setUser(user);
                  });
                });
              } else {
                oidcSecurityService.getAccessToken()
                  .pipe(
                    filter(value => {
                      if (value) {
                        return true;
                      }
                      return false;
                    }),
                  )
                  .subscribe(accessToken => {
                    const decoded = jwtDecode<JwtPayload>(accessToken);
                    const token = decoded as any;

                    const email = token.email;
                    const username = token.preferred_username;
                    const name = token.name;

                    Sentry.withScope(scope => {
                      let user = scope.getUser();
                      if (!user) {
                        user = {};
                      }
                      user.id = decoded.sub;
                      user.email = email;
                      user.username = username;
                      user['name'] = name;

                      scope.setUser(user);
                    });
                  });
              }
            }),
          );
      },
      deps: [ConfigService, EmbeddedService, OidcSecurityService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (
        configService: ConfigService,
        matomoInitializerService: MatomoInitializerService,
      ) => () => {
        return configService.loaded$
          .pipe(
            take(1),
            tap(config => {
              const matomoConfig = config.matomo;

              if (matomoConfig?.enabled
                && matomoConfig?.trackerUrl
                && matomoConfig?.siteId) {

                matomoInitializerService.initializeTracker({
                  trackerUrl: matomoConfig.trackerUrl,
                  siteId: matomoConfig.siteId,
                });

              }
            }),
          );
      },
      deps: [ConfigService, MatomoInitializerService],
      multi: true,
    },
    {
      // INFO: Injection Token für jf-ng-components (TableComponent benötigt Logger)
      provide: NGX_LOGGER,
      useClass: NGXLogger,
    },
  ],
  bootstrap: [
    AppComponent
  ],
})
export class AppModule {
}
